import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BaseService } from '../../services/base.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { IAllColumnsResponse, IColumn, IFunnel } from '../../interfaces';
import { CrmService } from '../../services/crm.service';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from '../../services/notifications.service';
import { CreateColumnComponent } from '../../modals/create-column/create-column.component';
import { ModalService } from '../../services/modal.service';
import { DialogService } from '../../services/dialog.service';
import { activeTranslations } from '../../enum/constants';

@Component({
  selector: 'app-tab-funnel-columns',
  templateUrl: './tab-funnel-columns.component.html',
  styleUrls: ['./tab-funnel-columns.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TabFunnelColumnsComponent implements OnInit {
  // Destroys
  private readonly destroy$: Subject<null> = new Subject<null>();

  // Inputs
  @Input() funnelData!: IFunnel;

  // Var-s
  public items: IColumn[] = [];
  public isLoader: boolean = false;

  constructor(
    public readonly dialog: MatDialog,
    public readonly baseService: BaseService,
    public readonly crmService: CrmService,
    private notificationService: NotificationService,
    private modalService: ModalService,
    private dialogService: DialogService,
  ) {}

  ngOnInit() {
    this.onGetAllColumns();
  }

  public onGetAllColumns(isPositionUpdate: boolean = false) {
    if (!isPositionUpdate) this.isLoader = true;
    const payload = {
      funnel_id: this.funnelData.id,
      page: 1,
      take: 50,
    };

    this.crmService
      .getAllColumns(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: IAllColumnsResponse) => {
          this.items = [...res.items];
          this.isLoader = false;
        },
        error: () => {
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.error_getting_columns,
          );
          this.isLoader = false;
        },
      });
  }

  public openCreateModal(item?: IColumn) {
    const position: number = this.calcAddedPosition(item);

    const dialogData = {
      title: 'create_column',
      translate: true,
      data: {
        funnel_id: this.funnelData.id,
        position: position,
      },
      component: CreateColumnComponent,
    };

    this.modalService.openModal(dialogData).subscribe((result) => {
      if (result) {
        this.onGetAllColumns();
      }
    });
  }

  public drop(event: CdkDragDrop<string[]>): void {
    if (this.items[event.currentIndex].id === this.items[event.previousIndex].id) return;

    const position: number = this.calcMovedPosition(event.currentIndex, event.previousIndex);
    moveItemInArray(this.items, event.previousIndex, event.currentIndex);

    this.crmService
      .updateColumn({ position: position }, this.items[event.currentIndex].id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.showSuccess(
            '',
            activeTranslations.notifications_texts.success.successfully_moved,
          );
          this.onGetAllColumns(true);
        },
        error: (err) => {
          console.log(err);
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.error_update_column_position,
          );
        },
      });
  }

  private calcMovedPosition(cp: number, pp: number): number {
    if (cp === 0) return this.items[0].position / 2;

    if (cp === this.items.length - 1) {
      const lastPosition: number = this.items[this.items.length - 1].position;

      return (lastPosition + (lastPosition + 1000)) / 2;
    }

    if (cp > pp) return (this.items[cp].position + this.items[cp + 1].position) / 2;
    if (cp < pp) return (this.items[cp].position + this.items[cp - 1].position) / 2;

    return (this.items[cp + 1].position + this.items[cp - 1].position) / 2;
  }

  private calcAddedPosition(item?: IColumn): number {
    if (item) {
      const cp: number = this.items.indexOf(item);
      if (cp === 0) {
        if (this.items[1]?.position) {
          return (this.items[0].position + this.items[1].position) / 2;
        } else {
          return (this.items[0].position + 1000) / 2;
        }
      } else if (cp === this.items.length - 1) {
        return (this.items[cp].position + (this.items[cp].position + 1000)) / 2;
      } else {
        return (this.items[cp].position + this.items[cp + 1].position) / 2;
      }
    } else {
      return 1000;
    }
  }

  onDeleteColumn(column: IColumn) {
    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.tab-funnel-columns',
    });

    dialogRef.subscribe({
      next: (result: any) => {
        if (result) {
          this.crmService
            .deleteColumn(column.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.notificationService.showSuccess(
                  '',
                  activeTranslations.notifications_texts.success.successfully_deleted,
                );
                this.onGetAllColumns();
              },
              error: () => {
                this.notificationService.showError(
                  '',
                  activeTranslations.notifications_texts.error.error_delete_column,
                );
              },
            });
        }
      },
      error: () => {
        this.notificationService.showError(
          '',
          activeTranslations.notifications_texts.error.something_went_wrong_1,
        );
      },
    });
  }

  onUpdateColumn(column: IColumn) {
    const dialogData = {
      title: 'change_column',
      translate: true,
      data: {
        funnel_id: this.funnelData.id,
        column: column,
      },
      component: CreateColumnComponent,
    };

    this.modalService.openModal(dialogData).subscribe((result) => {
      if (result) this.onGetAllColumns();
    });
  }
}
